// Parses CSV or Excel data (as a 2D string array) into Client import entries.
//
// Two file layouts are supported: a "legacy" single-header-row format and a
// "branded" two-header-row format. The parser auto-detects which layout is
// present by looking for a row whose first cell is "First name".
//
// ──────────────────────────────────────────────────────────────────────────────
// LEGACY FORMAT (single header row)
// ──────────────────────────────────────────────────────────────────────────────
//
// Row 1 is the header. Every subsequent non-empty row is one care recipient
// with one or more carers. Column names are case-insensitive and extra
// whitespace is ignored.
//
//   Care recipient columns (required unless noted):
//     Care recipient first name
//     Care recipient last name
//     Care recipient date of birth
//     Support area                           — one or more known speech need ids or names, separated by semicolons, commas or newlines
//     Notes (optional)                       — also accepted as "Care recipient notes",
//                                              "Notes (optional)", "Care recipient notes (optional)"
//
//   Carer columns (repeated for Carer 1, Carer 2, ...):
//     Carer 1 first name                     — required
//     Carer 1 last name                      — required
//     Carer 1 phone number                   — required
//     Carer 1 email (optional)               — also accepted as "Carer 1 email address",
//                                              "Carer 1 email address (optional)"
//     Carer 1 notes (optional)
//     Carer 1 comms campaign (optional)      — "yes" to enrol in the default campaign, "no" or empty to skip
//
//   At least one carer (Carer 1) with first name, last name, and phone number
//   is required per row. Additional carers use the same pattern with an
//   incremented number (Carer 2, Carer 3, ...).
//
//   Example:
//
//   | Care recipient first name | Care recipient last name | Care recipient date of birth | Support area | Carer 1 first name | Carer 1 last name | Carer 1 phone number | Carer 1 email (optional) | Carer 1 comms campaign (optional) |
//   |---------------------------|--------------------------|-----------------------------|--------------|--------------------|-------------------|----------------------|--------------------------|-----------------------------------|
//   | Alice                     | Smith                    | 2019-03-15                  | Stammering   | Bob                | Smith             | +447700900123        | bob@example.com          | yes                               |
//
// ──────────────────────────────────────────────────────────────────────────────
// BRANDED FORMAT (two header rows)
// ──────────────────────────────────────────────────────────────────────────────
//
// Designed for branded Excel templates that may have logos or titles above the
// actual headers. The parser scans for a row whose first cell is "First name"
// and treats the row immediately above it as the section header row.
//
//   Section header row (row N-1):
//     Cells label the section each group of columns belongs to. A section
//     label applies to all columns to its right until the next label.
//     Recognised sections: "Care recipient", "Carer 1", "Carer 2", etc.
//
//   Field header row (row N):
//     Cells name the field within the current section. Recognised fields:
//       First name, Last name, Date of birth, Support area, Notes,
//       Phone / Phone number, Email, Comms campaign /
//       Carer communications campaign (sms/email)
//
//   Data rows start at row N+1. Empty rows are ignored.
//
//   Example (rows shown top to bottom):
//
//   Row 1: | (logo / title — ignored)     |             |                |              |              |             |         |       |                                          |
//   Row 2: | Care recipient               |             |                |              | Carer 1      |             |         |       |                                          |
//   Row 3: | First name                   | Last name   | Date of birth  | Support area | First name   | Last name   | Phone   | Email | Carer communications campaign (sms/email) |
//   Row 4: | Alice                        | Smith       | 15/03/2019     | Stammering   | Bob          | Smith       | 07700.. | bob@  | yes                                      |
//
// ──────────────────────────────────────────────────────────────────────────────
// NOTES
// ──────────────────────────────────────────────────────────────────────────────
//
// - Phone numbers are normalised to E.164 format assuming a GB (+44) default.
// - The "support area" cell must contain one or more ids or display names of
//   SpeechNeeds configured for the healthcare provider. Multiple values can be
//   separated with semicolons, commas or newlines. Names are matched ignoring
//   case and repeated whitespace; ambiguous names must be replaced with ids.
//   Numbered columns such as "Support area 1", "Support area 2", and "Support
//   area 3" are also accepted, with at least one selection across the columns.
// - The "comms campaign" cell accepts "yes" (enrol in the provider's default
//   campaign), "no", or empty (skip enrolment). Any other value is an error.
// - Rows where neither care recipient first name nor last name is provided are
//   silently skipped (treated as blank/template rows).

import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Client, SpeechNeed } from '../interfaces/Frameworks';
import { withClientSpeechNeeds } from '../clientSpeechNeeds';

type CareRecipientField = 'givenName' | 'familyName' | 'dateOfBirth' | 'supportArea' | 'notes';
type CarerField = 'givenName' | 'familyName' | 'phoneNumber' | 'email' | 'notes' | 'commsCampaignId';

type CarerFieldMap = Map<number, Map<CarerField, number>>;
type SupportAreaColumn = { index: number; label: string };

export type ClientsImportEntry = {
    rowNumber: number;
    client: Client;
    carers: Client[];
};

export type ClientsImportResult = {
    importedRows: number;
    importedClients: number;
    importedCarers: number;
    reusedCarers: number;
};

export type ParseClientsImportOptions = {
    defaultCommsCampaignId?: string;
};

const CARE_RECIPIENT_HEADER_ALIASES: Record<string, CareRecipientField> = {
    'care recipient first name': 'givenName',
    'care recipient last name': 'familyName',
    'care recipient date of birth': 'dateOfBirth',
    'support area': 'supportArea',
    'support areas': 'supportArea',
    'speech need': 'supportArea',
    'speech needs': 'supportArea',
    'notes': 'notes',
    'notes (optional)': 'notes',
    'care recipient notes': 'notes',
    'care recipient notes (optional)': 'notes',
};

const BRANDED_CARE_RECIPIENT_FIELD_ALIASES: Record<string, CareRecipientField> = {
    'first name': 'givenName',
    'last name': 'familyName',
    'date of birth': 'dateOfBirth',
    'support area': 'supportArea',
    'support areas': 'supportArea',
    'speech need': 'supportArea',
    'speech needs': 'supportArea',
    'notes': 'notes',
};

const CARER_HEADER_PATTERNS: Array<{ pattern: RegExp; field: CarerField }> = [
    { pattern: /^carer (\d+) first name$/, field: 'givenName' },
    { pattern: /^carer (\d+) last name$/, field: 'familyName' },
    { pattern: /^carer (\d+) phone number$/, field: 'phoneNumber' },
    { pattern: /^carer (\d+) email(?: address)?(?: \(optional\))?$/, field: 'email' },
    { pattern: /^carer (\d+) notes(?: \(optional\))?$/, field: 'notes' },
    { pattern: /^carer (\d+) comms campaign(?: \(optional\))?$/, field: 'commsCampaignId' },
];

const BRANDED_CARER_FIELD_ALIASES: Record<string, CarerField> = {
    'first name': 'givenName',
    'last name': 'familyName',
    'phone': 'phoneNumber',
    'phone number': 'phoneNumber',
    'email': 'email',
    'comms campaign': 'commsCampaignId',
    'carer communications campaign (sms/email)': 'commsCampaignId',
    'notes': 'notes',
};

const SUPPORT_AREA_HEADER_PATTERN = /^(?:support areas?|speech needs?)(?: [1-9]\d*)?$/;
const REQUIRED_CARE_RECIPIENT_FIELDS: CareRecipientField[] = ['givenName', 'familyName', 'dateOfBirth', 'supportArea'];
const REQUIRED_CARER_FIELDS: CarerField[] = ['givenName', 'familyName', 'phoneNumber'];

const CARE_RECIPIENT_FIELD_LABELS: Record<CareRecipientField, string> = {
    givenName: 'Care recipient first name',
    familyName: 'Care recipient last name',
    dateOfBirth: 'Care recipient date of birth',
    supportArea: 'Support area',
    notes: 'Notes',
};

const CARER_FIELD_LABELS: Record<CarerField, string> = {
    givenName: 'first name',
    familyName: 'last name',
    phoneNumber: 'phone number',
    email: 'email address',
    notes: 'notes',
    commsCampaignId: 'comms campaign',
};

function normaliseHeader(header: string): string {
    return header.replace(/^\uFEFF/, '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function formatPhoneNumber(phoneNumber: string): string {
    const trimmedPhoneNumber = phoneNumber.trim();
    const parsedPhoneNumber = parsePhoneNumberFromString(trimmedPhoneNumber, 'GB');
    return parsedPhoneNumber ? parsedPhoneNumber.format('E.164') : trimmedPhoneNumber;
}

function parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentValue = '';
    let inQuotes = false;

    const input = text.replace(/^\uFEFF/, '');

    for (let i = 0; i < input.length; i += 1) {
        const character = input[i];

        if (inQuotes) {
            if (character === '"') {
                if (input[i + 1] === '"') {
                    currentValue += '"';
                    i += 1;
                } else {
                    inQuotes = false;
                }
            } else {
                currentValue += character;
            }
            continue;
        }

        if (character === '"') {
            inQuotes = true;
            continue;
        }

        if (character === ',') {
            currentRow.push(currentValue);
            currentValue = '';
            continue;
        }

        if (character === '\n' || character === '\r') {
            if (character === '\r' && input[i + 1] === '\n') {
                i += 1;
            }
            currentRow.push(currentValue);
            rows.push(currentRow);
            currentRow = [];
            currentValue = '';
            continue;
        }

        currentValue += character;
    }

    if (inQuotes) {
        throw new Error('Malformed CSV file: missing closing quote.');
    }

    currentRow.push(currentValue);
    if (currentRow.some(value => value.length > 0) || rows.length === 0) {
        rows.push(currentRow);
    }

    return rows;
}

// Allow harmless typing differences without guessing which support area was intended.
function normaliseSupportArea(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

// Explicit ids take precedence so they can safely disambiguate duplicate display names.
function getMatchingSupportAreaIds(value: string, speechNeeds: SpeechNeed[]): string[] {
    const normalisedValue = normaliseSupportArea(value);
    if (!normalisedValue) {
        return [];
    }
    const idMatches = speechNeeds.filter(speechNeed => normaliseSupportArea(speechNeed.id) === normalisedValue);
    const matches = idMatches.length > 0
        ? idMatches
        : speechNeeds.filter(speechNeed => normaliseSupportArea(speechNeed.name) === normalisedValue);
    return Array.from(new Set(matches.map(speechNeed => speechNeed.id)));
}

// Use a readable name only when it remains unambiguous when joined into a multi-value cell.
export function getSupportAreaImportValue(speechNeed: SpeechNeed, speechNeeds: SpeechNeed[]): string {
    const matchingIds = getMatchingSupportAreaIds(speechNeed.name, speechNeeds);
    return !/[,;\r\n]/.test(speechNeed.name) && matchingIds.length === 1 && matchingIds[0] === speechNeed.id
        ? speechNeed.name.trim()
        : speechNeed.id;
}

// Resolve each supplied value and retain all invalid tokens for actionable row errors.
function getSupportAreaIds(supportAreaValue: string, speechNeeds: SpeechNeed[]) {
    const unknownValues: string[] = [];
    const ambiguousValues: Array<{ value: string; ids: string[] }> = [];
    const speechNeedIds: string[] = [];
    const supportAreaValues = supportAreaValue.split(/[;\r\n]+/).flatMap((chunk) => {
        const trimmedChunk = chunk.trim();
        // Preserve configured names containing commas before treating commas as separators.
        if (trimmedChunk && getMatchingSupportAreaIds(trimmedChunk, speechNeeds).length > 0) {
            return [trimmedChunk];
        }
        return chunk.split(',').map(value => value.trim()).filter(Boolean);
    });

    supportAreaValues.forEach((value) => {
        const matchingIds = getMatchingSupportAreaIds(value, speechNeeds);
        if (matchingIds.length === 0) {
            unknownValues.push(value);
        } else if (matchingIds.length > 1) {
            ambiguousValues.push({ value, ids: matchingIds });
        } else {
            speechNeedIds.push(matchingIds[0]);
        }
    });

    return { speechNeedIds: Array.from(new Set(speechNeedIds)), unknownValues, ambiguousValues };
}

// Include the provider's own choices, with ids where names cannot be pasted safely.
function getAvailableSupportAreasMessage(speechNeeds: SpeechNeed[]): string {
    if (speechNeeds.length === 0) {
        return 'No support areas are configured for this healthcare provider.';
    }
    const availableValues = speechNeeds.map(speechNeed => (
        getSupportAreaImportValue(speechNeed, speechNeeds) === speechNeed.name.trim()
            ? `"${speechNeed.name.trim()}"`
            : `"${speechNeed.name.trim()}" (ID: "${speechNeed.id}")`
    ));
    return `Available support areas for this healthcare provider: ${availableValues.join('; ')}.`;
}

function buildHeaderMaps(headers: string[]) {
    const careRecipientFields = new Map<CareRecipientField, number>();
    const supportAreaColumns: SupportAreaColumn[] = [];
    const carerFields: CarerFieldMap = new Map();

    headers.forEach((header, index) => {
        const normalisedHeader = normaliseHeader(header);
        // Retain every support area column, including repeated aliases, in spreadsheet order.
        if (SUPPORT_AREA_HEADER_PATTERN.test(normalisedHeader)) {
            supportAreaColumns.push({ index, label: header.trim() });
            return;
        }
        const careRecipientField = CARE_RECIPIENT_HEADER_ALIASES[normalisedHeader];
        if (careRecipientField) {
            careRecipientFields.set(careRecipientField, index);
            return;
        }

        for (const pattern of CARER_HEADER_PATTERNS) {
            const match = normalisedHeader.match(pattern.pattern);
            if (!match) {
                continue;
            }

            const carerIndex = Number.parseInt(match[1], 10);
            const carerFieldMap = carerFields.get(carerIndex) ?? new Map<CarerField, number>();
            carerFieldMap.set(pattern.field, index);
            carerFields.set(carerIndex, carerFieldMap);
            return;
        }
    });

    return { careRecipientFields, supportAreaColumns, carerFields };
}

function buildBrandedHeaderMaps(sectionHeaders: string[], fieldHeaders: string[]) {
    const careRecipientFields = new Map<CareRecipientField, number>();
    const supportAreaColumns: SupportAreaColumn[] = [];
    const carerFields: CarerFieldMap = new Map();
    let currentSection = '';
    const columnCount = Math.max(sectionHeaders.length, fieldHeaders.length);

    for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
        const sectionHeader = normaliseHeader(sectionHeaders[columnIndex] ?? '');
        if (sectionHeader) {
            currentSection = sectionHeader;
        }

        const fieldHeader = normaliseHeader(fieldHeaders[columnIndex] ?? '');
        if (!fieldHeader || !currentSection) {
            continue;
        }

        if (currentSection === 'care recipient') {
            // Numbered and original support area headers can coexist without overwriting each other.
            if (SUPPORT_AREA_HEADER_PATTERN.test(fieldHeader)) {
                supportAreaColumns.push({ index: columnIndex, label: fieldHeaders[columnIndex].trim() });
                continue;
            }
            const careRecipientField = BRANDED_CARE_RECIPIENT_FIELD_ALIASES[fieldHeader];
            if (careRecipientField) {
                careRecipientFields.set(careRecipientField, columnIndex);
            }
            continue;
        }

        const carerMatch = currentSection.match(/^carer (\d+)$/);
        if (!carerMatch) {
            continue;
        }

        const carerField = BRANDED_CARER_FIELD_ALIASES[fieldHeader];
        if (!carerField) {
            continue;
        }

        const carerIndex = Number.parseInt(carerMatch[1], 10);
        const carerFieldMap = carerFields.get(carerIndex) ?? new Map<CarerField, number>();
        carerFieldMap.set(carerField, columnIndex);
        carerFields.set(carerIndex, carerFieldMap);
    }

    return { careRecipientFields, supportAreaColumns, carerFields };
}

function validateHeaderMaps(
    careRecipientFields: Map<CareRecipientField, number>,
    supportAreaColumns: SupportAreaColumn[],
    carerFields: CarerFieldMap
) {
    // A single original or numbered support area column satisfies the header requirement.
    const missingCareRecipientHeaders = REQUIRED_CARE_RECIPIENT_FIELDS.filter(field => (
        field === 'supportArea' ? supportAreaColumns.length === 0 : !careRecipientFields.has(field)
    ));
    if (missingCareRecipientHeaders.length > 0) {
        throw new Error(`Row 1 (header): missing required CSV columns: ${missingCareRecipientHeaders.map(field => CARE_RECIPIENT_FIELD_LABELS[field]).join(', ')}`);
    }

    if (carerFields.size === 0) {
        throw new Error('Row 1 (header): missing carer columns. Expected at least Carer 1 first name, last name, and phone number.');
    }

    const incompleteCarerHeaders: string[] = [];
    Array.from(carerFields.entries())
        .sort((a, b) => a[0] - b[0])
        .forEach(([carerIndex, fieldMap]) => {
            const missingFields = REQUIRED_CARER_FIELDS.filter(field => !fieldMap.has(field));
            if (missingFields.length > 0) {
                incompleteCarerHeaders.push(`Carer ${carerIndex}: ${missingFields.map(field => CARER_FIELD_LABELS[field]).join(', ')}`);
            }
        });

    if (incompleteCarerHeaders.length > 0) {
        throw new Error(`Row 1 (header): missing required carer CSV columns: ${incompleteCarerHeaders.join('; ')}`);
    }
}

function getCellValue(row: string[], index: number | undefined): string {
    if (index === undefined) {
        return '';
    }
    return (row[index] ?? '').trim();
}

function getCommsCampaignId(
    commsCampaignCellValue: string,
    defaultCommsCampaignId: string | undefined,
    rowNumber: number,
    validationErrors: string[]
): string | undefined {
    const normalisedValue = commsCampaignCellValue.trim().toLowerCase();
    if (!normalisedValue || normalisedValue === 'no') {
        return undefined;
    }

    if (normalisedValue === 'yes') {
        if (!defaultCommsCampaignId) {
            validationErrors.push(`Row ${rowNumber}: no default comms campaign is configured for this healthcare provider.`);
            return undefined;
        }
        return defaultCommsCampaignId;
    }

    validationErrors.push(`Row ${rowNumber}: invalid carer communications campaign value "${commsCampaignCellValue}". Expected "yes", "no", or empty.`);
    return undefined;
}

function parseClientRows(
    dataRows: Array<{ row: string[]; rowNumber: number }>,
    careRecipientFields: Map<CareRecipientField, number>,
    supportAreaColumns: SupportAreaColumn[],
    carerFields: CarerFieldMap,
    speechNeeds: SpeechNeed[],
    options: ParseClientsImportOptions
): ClientsImportEntry[] {
    const validationErrors: string[] = [];
    const entries: ClientsImportEntry[] = [];
    const sortedCarerIndexes = Array.from(carerFields.keys()).sort((a, b) => a - b);

    dataRows.forEach(({ row, rowNumber }) => {

        const careRecipientFirstName = getCellValue(row, careRecipientFields.get('givenName'));
        const careRecipientLastName = getCellValue(row, careRecipientFields.get('familyName'));
        const careRecipientDateOfBirth = getCellValue(row, careRecipientFields.get('dateOfBirth'));
        const supportAreaCells = supportAreaColumns.map(column => ({
            label: column.label,
            value: getCellValue(row, column.index),
        }));
        const careRecipientNotes = getCellValue(row, careRecipientFields.get('notes'));

        // Ignore template rows that don't identify a care recipient.
        if (!careRecipientFirstName && !careRecipientLastName) {
            return;
        }

        const missingCareRecipientFields: string[] = [];
        if (!careRecipientFirstName) missingCareRecipientFields.push('care recipient first name');
        if (!careRecipientLastName) missingCareRecipientFields.push('care recipient last name');
        if (!careRecipientDateOfBirth) missingCareRecipientFields.push('care recipient date of birth');
        if (!supportAreaCells.some(cell => cell.value.replace(/[,;\r\n]/g, '').trim())) missingCareRecipientFields.push('support area');

        // Parse cells independently so separate selections cannot form a different comma-containing name.
        const parsedSupportAreas = supportAreaCells.map(cell => ({
            ...getSupportAreaIds(cell.value, speechNeeds),
            label: cell.label,
        }));
        const speechNeedIds = Array.from(new Set(parsedSupportAreas.flatMap(cell => cell.speechNeedIds)));
        const supportAreaErrors: string[] = [];
        parsedSupportAreas.forEach(({ unknownValues, ambiguousValues, label }) => {
            const cellErrors: string[] = [];
            if (unknownValues.length > 0) {
                cellErrors.push(`unknown support area${unknownValues.length > 1 ? 's' : ''}: ${unknownValues.map(value => `"${value}"`).join(', ')}.`);
            }
            ambiguousValues.forEach(({ value, ids }) => {
                cellErrors.push(`ambiguous support area "${value}". Use one of these IDs: ${ids.map(id => `"${id}"`).join(', ')}.`);
            });
            if (cellErrors.length > 0) {
                const columnContext = supportAreaColumns.length > 1 ? ` Column: "${label}".` : '';
                supportAreaErrors.push(`${cellErrors.join(' ')}${columnContext}`);
            }
        });
        // Report every invalid selection and give users valid values from this provider.
        if (supportAreaErrors.length > 0) {
            validationErrors.push(`Row ${rowNumber}: ${supportAreaErrors.join(' ')} ${getAvailableSupportAreasMessage(speechNeeds)}`);
            return;
        }

        if (missingCareRecipientFields.length > 0) {
            validationErrors.push(`Row ${rowNumber}: missing ${missingCareRecipientFields.join(', ')}.`);
            return;
        }

        const carers: Client[] = [];
        let hasAnyCarer = false;

        sortedCarerIndexes.forEach((carerIndex) => {
            const fieldMap = carerFields.get(carerIndex);
            if (!fieldMap) {
                return;
            }

            const givenName = getCellValue(row, fieldMap.get('givenName'));
            const familyName = getCellValue(row, fieldMap.get('familyName'));
            const phoneNumber = getCellValue(row, fieldMap.get('phoneNumber'));
            const email = getCellValue(row, fieldMap.get('email'));
            const notes = getCellValue(row, fieldMap.get('notes'));
            const commsCampaignValue = getCellValue(row, fieldMap.get('commsCampaignId'));

            const hasCarerData = [givenName, familyName, phoneNumber, email, notes, commsCampaignValue].some(value => value.length > 0);
            if (!hasCarerData) {
                return;
            }

            hasAnyCarer = true;

            const missingCarerFields: string[] = [];
            if (!givenName) missingCarerFields.push(`Carer ${carerIndex} first name`);
            if (!familyName) missingCarerFields.push(`Carer ${carerIndex} last name`);
            if (!phoneNumber) missingCarerFields.push(`Carer ${carerIndex} phone number`);

            if (missingCarerFields.length > 0) {
                validationErrors.push(`Row ${rowNumber}: missing ${missingCarerFields.join(', ')}.`);
                return;
            }

            const commsCampaignId = getCommsCampaignId(
                commsCampaignValue,
                options.defaultCommsCampaignId,
                rowNumber,
                validationErrors
            );

            if (validationErrors.some(error => error.startsWith(`Row ${rowNumber}:`))) {
                return;
            }

            carers.push({
                userType: 'carer',
                givenName,
                familyName,
                phoneNumber: formatPhoneNumber(phoneNumber),
                ...(email ? { email } : {}),
                ...(notes ? { notes } : {}),
                ...(commsCampaignId ? { commsCampaignId } : {}),
            });
        });

        if (!hasAnyCarer) {
            validationErrors.push(`Row ${rowNumber}: at least one carer is required.`);
            return;
        }

        if (validationErrors.some(error => error.startsWith(`Row ${rowNumber}:`))) {
            return;
        }

        entries.push({
            rowNumber,
            client: withClientSpeechNeeds({
                userType: 'care_recipient',
                givenName: careRecipientFirstName,
                familyName: careRecipientLastName,
                dateOfBirth: careRecipientDateOfBirth,
                conditionIds: [],
                speechNeeds: speechNeedIds,
                ...(careRecipientNotes ? { notes: careRecipientNotes } : {}),
            }),
            carers,
        });
    });

    if (validationErrors.length > 0) {
        throw new Error(validationErrors.join('\n'));
    }

    return entries;
}

export function parseClientsImportRows(
    rows: string[][],
    speechNeeds: SpeechNeed[],
    options: ParseClientsImportOptions = {}
): ClientsImportEntry[] {
    if (rows.length === 0) {
        return [];
    }

    const brandedFieldHeaderRowIndex = rows.findIndex((row) => normaliseHeader(row[0] ?? '') === 'first name');

    if (brandedFieldHeaderRowIndex >= 1) {
        const sectionHeaders = rows[brandedFieldHeaderRowIndex - 1];
        const fieldHeaders = rows[brandedFieldHeaderRowIndex];
        const { careRecipientFields, supportAreaColumns, carerFields } = buildBrandedHeaderMaps(sectionHeaders, fieldHeaders);
        validateHeaderMaps(careRecipientFields, supportAreaColumns, carerFields);

        const dataRows: Array<{ row: string[]; rowNumber: number }> = [];
        for (let rowIndex = brandedFieldHeaderRowIndex + 1; rowIndex < rows.length; rowIndex += 1) {
            const row = rows[rowIndex];
            // Skip unused template rows without losing later contacts or their spreadsheet row numbers.
            if (!row.some(value => value.trim().length > 0)) {
                continue;
            }
            dataRows.push({ row, rowNumber: rowIndex + 1 });
        }

        return parseClientRows(dataRows, careRecipientFields, supportAreaColumns, carerFields, speechNeeds, options);
    }

    // Retain original spreadsheet row numbers while blank rows are ignored below.
    const [headerRow, ...legacyDataRows] = rows;
    const { careRecipientFields, supportAreaColumns, carerFields } = buildHeaderMaps(headerRow);
    validateHeaderMaps(careRecipientFields, supportAreaColumns, carerFields);

    return parseClientRows(
        legacyDataRows.map((row, index) => ({ row, rowNumber: index + 2 })),
        careRecipientFields,
        supportAreaColumns,
        carerFields,
        speechNeeds,
        options
    );
}

// Share the parser's exact header mapping with spreadsheet date normalization.
export function getClientsImportDateOfBirthColumn(rows: string[][]): number | undefined {
    if (rows.length === 0) {
        return undefined;
    }
    const brandedHeaderIndex = rows.findIndex(row => normaliseHeader(row[0] ?? '') === 'first name');
    const { careRecipientFields } = brandedHeaderIndex >= 1
        ? buildBrandedHeaderMaps(rows[brandedHeaderIndex - 1], rows[brandedHeaderIndex])
        : buildHeaderMaps(rows[0]);
    return careRecipientFields.get('dateOfBirth');
}

export function parseClientsImportCsv(
    text: string,
    speechNeeds: SpeechNeed[],
    options: ParseClientsImportOptions = {}
): ClientsImportEntry[] {
    return parseClientsImportRows(parseCsv(text), speechNeeds, options);
}
